import type { Content } from '~api';
import type { Brand, OperatorGroup, ShareListing } from './types';

/**
 * The portal's company directory (openspec change `operator-groups-in-sanity`, D5): which companies
 * exist and who they are come from the Sanity `operatorGroup` documents (`content.operatorGroups`);
 * every figure — GGR, financials, regional split, market share, brands, the generated feed — stays
 * in the illustrative fixture (`operators.ts`), laid over the directory by slug. A directory
 * company the fixture does not know renders its identity only; a fixture group the directory does
 * not list is not shown. Pure, so the hooks, Home's tracked pages and the tests merge identically.
 *
 * A company's brands follow the same rule (openspec change `operator-brands-in-sanity`, D5): they
 * are the Sanity `operator` documents whose group is the company (`content.operators`), each with
 * the fixture brand of the same slug — or, failing that, the same name — laid over it. A brand the
 * fixture lacks renders its identity only; a fixture brand Sanity does not list is not shown.
 */

/** @desc The fixture fields a company with no fixture entry gets: empty, never invented. */
const identityOnly = (entry: Content.OperatorGroup): OperatorGroup => ({
  slug: entry.slug,
  name: entry.name,
  classification: entry.classification ?? 'operator-b2c',
  hq: entry.hq ?? '',
  hqMarket: entry.hqMarket ?? '',
  founder: '',
  listings: [],
  sectors: [],
  footprint: entry.description ?? '',
  maSummary: '',
  commentary: entry.description ?? '',
  status: 'active',
  acquisitions: [],
  brands: [],
  operatingRegions: [],
  news: [],
  marketUpdates: [],
  callSummaries: [],
  downloads: [],
  identityOnly: true,
});

/** @desc The contract's listing as the portal types it; the Studio limits currencies to the portal's. */
const toListing = (listing: Content.ShareListing): ShareListing => ({
  ...listing,
  currency: listing.currency as ShareListing['currency'],
});

/** @desc One directory company with its fixture figures, if any. */
export const mergeOperatorGroup = (
  entry: Content.OperatorGroup,
  fixture: OperatorGroup | undefined,
): OperatorGroup => {
  const base = fixture ?? identityOnly(entry);
  const founded = entry.founded ?? fixture?.founded;
  return {
    ...base,
    name: entry.name,
    classification: entry.classification ?? base.classification,
    hq: entry.hq ?? base.hq,
    hqMarket: entry.hqMarket ?? base.hqMarket,
    ...(founded === undefined ? {} : { founded }),
    listings: entry.listings.map(toListing),
    ...(entry.description ? { description: entry.description } : {}),
    // A logo marked not cleared is not shown (design D8); unreviewed ones are.
    ...(entry.logoUrl && entry.logoLicence !== 'not-cleared' ? { logoUrl: entry.logoUrl } : {}),
  };
};

/** @desc A name compared ignoring case, spaces and punctuation: `Bet 365` and `bet365` agree. */
export const brandNameKey = (name: string): string =>
  name
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** @desc A Sanity brand the fixture has no entry for: identity only, no figures invented. */
const identityOnlyBrand = (entry: Content.Operator, groupSlug: string): Brand => ({
  slug: entry.slug,
  name: entry.name,
  groupSlug,
  type: 'operator-b2c',
  description: entry.description ?? '',
  hqMarket: '',
  operatingRegions: [],
  identityOnly: true,
});

/** @desc One Sanity brand with its fixture figures, if any, under the company `groupSlug`. */
export const mergeBrand = (
  entry: Content.Operator,
  groupSlug: string,
  fixture: Brand | undefined,
): Brand => {
  const base = fixture ?? identityOnlyBrand(entry, groupSlug);
  return {
    ...base,
    slug: entry.slug,
    name: entry.name,
    groupSlug,
    description: entry.description ?? base.description,
    // A logo marked not cleared is not shown (groups design D8); unreviewed ones are.
    ...(entry.logoUrl && entry.logoLicence !== 'not-cleared' ? { logoUrl: entry.logoUrl } : {}),
  };
};

/**
 * @desc Each company's brands: the Sanity brands whose group it is, the fixture's own brands of
 * that company first in fixture order (its flagship leads the carousel), then the rest by name.
 */
const brandsByGroup = (
  brands: readonly Content.Operator[],
  fixture: readonly OperatorGroup[],
): Map<string, Brand[]> => {
  const fixtureBrands = fixture.flatMap((group) => group.brands);
  const bySlug = new Map(fixtureBrands.map((brand) => [brand.slug, brand]));
  const byName = new Map(fixtureBrands.map((brand) => [brandNameKey(brand.name), brand]));
  const order = new Map(fixtureBrands.map((brand, index) => [brand.slug, index]));
  const grouped = new Map<string, Array<{ brand: Brand; rank: number }>>();
  for (const entry of brands) {
    if (!entry.groupSlug) continue;
    const match = bySlug.get(entry.slug) ?? byName.get(brandNameKey(entry.name));
    const sameGroup = match?.groupSlug === entry.groupSlug ? match : undefined;
    const brand = mergeBrand(entry, entry.groupSlug, match);
    const list = grouped.get(entry.groupSlug) ?? [];
    list.push({
      brand,
      rank: sameGroup ? (order.get(sameGroup.slug) ?? Infinity) : Infinity,
    });
    grouped.set(entry.groupSlug, list);
  }
  return new Map(
    [...grouped].map(([slug, list]) => [
      slug,
      list
        .sort((a, b) => a.rank - b.rank || a.brand.name.localeCompare(b.brand.name, 'en-GB'))
        .map(({ brand }) => brand),
    ]),
  );
};

/** @desc Stands in for "no brand list given" in the memo, which needs an object key. */
const FIXTURE_BRANDS: readonly Content.Operator[] = [];

const merged = new WeakMap<
  readonly Content.OperatorGroup[],
  WeakMap<readonly OperatorGroup[], WeakMap<readonly Content.Operator[], OperatorGroup[]>>
>();

/**
 * @desc The directory, in its order, with the fixture's figures laid over by slug and, when
 * `brands` is given, each company's brands from Sanity (without it the fixture's brands stay —
 * Home's tracked pages need only the companies). Memoised per (directory, fixture, brands), so
 * every component reading the same query results gets the same array and the same group objects.
 */
export function mergeOperatorGroups(
  directory: readonly Content.OperatorGroup[],
  fixture: readonly OperatorGroup[],
  brands: readonly Content.Operator[] = FIXTURE_BRANDS,
): OperatorGroup[] {
  const byFixture = merged.get(directory) ?? new WeakMap();
  merged.set(directory, byFixture);
  const byBrands = byFixture.get(fixture) ?? new WeakMap();
  byFixture.set(fixture, byBrands);
  const cached = byBrands.get(brands);
  if (cached) return cached;
  const bySlug = new Map(fixture.map((group) => [group.slug, group]));
  const grouped = brands === FIXTURE_BRANDS ? undefined : brandsByGroup(brands, fixture);
  const groups = directory.map((entry) => {
    const group = mergeOperatorGroup(entry, bySlug.get(entry.slug));
    return grouped ? { ...group, brands: grouped.get(entry.slug) ?? [] } : group;
  });
  byBrands.set(brands, groups);
  return groups;
}

/**
 * @desc A Sanity CDN logo URL sized for a tile of `size` px: twice the size for high-density
 * screens, never cropped (`fit=max`), and in the best format the browser takes (`auto=format`).
 */
export const sizedLogoUrl = (url: string | undefined, size: number): string | undefined => {
  if (!url) return undefined;
  const params = new URLSearchParams({
    w: String(size * 2),
    h: String(size * 2),
    fit: 'max',
    auto: 'format',
  });
  return `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
};
